import jsPDF from 'jspdf';
import { format } from 'date-fns';

export const generateExcursionPDF = async (booking) => {
  try {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    let y = 0;

    const currencies = {
      EUR: { symbol: '€', name: 'Euro' },
      USD: { symbol: '$', name: 'US Dollar' },
      GBP: { symbol: '£', name: 'British Pound' }
    };

    const getCurrencySymbol = (code) => currencies[code]?.symbol || '€';
    const currencySymbol = getCurrencySymbol(booking.pricing?.currency || booking.currency);

    const bookingNumber = booking.fullNumber || `EXC-${booking.number}`;

    const primaryColor = { r: 30, g: 58, b: 138 };

    const hasPricePerPerson = booking.pricing?.priceType === 'per_person';
    let excursionTotal = 0;
    
    if (hasPricePerPerson) {
      excursionTotal = ((booking.pricing?.adultPrice || 0) * (booking.pricing?.adultCount || 0)) + 
                       ((booking.pricing?.childPrice || 0) * (booking.pricing?.childCount || 0));
    } else {
      excursionTotal = booking.pricing?.groupPrice || 0;
    }

    const addWrappedText = (text, x, startY, maxWidth, fontSize = 10, lineHeight = 12) => {
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(text, maxWidth);
      let currentY = startY;
      
      lines.forEach(line => {
        if (currentY > pageHeight - 50) {
          doc.addPage();
          currentY = margin;
        }
        doc.text(line, x, currentY);
        currentY += lineHeight;
      });
      
      return currentY;
    };

    // --- HEADER ---
    doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.rect(0, 0, pageWidth, 100, 'F');
    
    y = 30;

    if (booking.companyInfo?.logoUrl) {
      try {
        const img = new Image();
        img.src = booking.companyInfo.logoUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        
        const maxWidth = 80;
        const maxHeight = 40;
        const imgAspectRatio = img.width / img.height;
        const boxAspectRatio = maxWidth / maxHeight;

        let logoWidth, logoHeight;

        if (imgAspectRatio > boxAspectRatio) {
          logoWidth = maxWidth;
          logoHeight = maxWidth / imgAspectRatio;
        } else {
          logoHeight = maxHeight;
          logoWidth = maxHeight * imgAspectRatio;
        }
        
        const logoY = y + (maxHeight - logoHeight) / 2;
        doc.addImage(booking.companyInfo.logoUrl, 'PNG', margin, logoY, logoWidth, logoHeight);
      } catch (e) {
        console.log('Logo could not be loaded.');
      }
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text('EXCURSION BOOKING CONFIRMATION', pageWidth - margin, y + 15, { align: 'right' });
    
    // --- STATUS LABEL ---
    const status = booking.status || 'paid';
    const isPaid = status === 'paid';
    const statusText = isPaid ? 'PAID' : 'UNPAID';
    const statusColor = isPaid ? [0, 128, 0] : [220, 38, 38];

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
    doc.text(statusText, pageWidth - margin, y + 35, { align: 'right' });

    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(`Booking #${bookingNumber}`, pageWidth - margin, y + 50, { align: 'right' });
    doc.text(`Date: ${format(new Date(booking.createdAt || Date.now()), 'MMMM dd, yyyy')}`, pageWidth - margin, y + 65, { align: 'right' });

    y = 120;

    // --- COMPANY INFO ---
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, y, pageWidth - (margin * 2), 60, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.rect(margin, y, pageWidth - (margin * 2), 60);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text(booking.companyInfo?.name || 'Travel Agency', margin + 15, y + 22);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let infoY = y + 38;
    if (booking.companyInfo?.phone) {
      doc.text(`Phone: ${booking.companyInfo.phone}`, margin + 15, infoY);
      infoY += 12;
    }
    if (booking.companyInfo?.email) {
      doc.text(`Email: ${booking.companyInfo.email}`, margin + 15, infoY);
    }

    y += 80;

    // --- CUSTOMER & EXCURSION INFO ---
    const cardWidth = (pageWidth - (margin * 2) - 20) / 2;
    
    doc.setFillColor(255, 255, 255);
    doc.rect(margin, y, cardWidth, 110, 'F');
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(1);
    doc.rect(margin, y, cardWidth, 110);

    doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.rect(margin, y, cardWidth, 25, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text('CUSTOMER INFORMATION', margin + 10, y + 16);

    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(booking.customer?.name || 'Customer Name', margin + 10, y + 45);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    let customerY = y + 60;
    if (booking.customer?.email) {
      doc.text(booking.customer.email, margin + 10, customerY);
      customerY += 14;
    }
    if (booking.customer?.phone) {
      doc.text(booking.customer.phone, margin + 10, customerY);
    }

    const excursionX = margin + cardWidth + 20;
    
    doc.setFillColor(255, 255, 255);
    doc.rect(excursionX, y, cardWidth, 110, 'F');
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.rect(excursionX, y, cardWidth, 110);

    doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.rect(excursionX, y, cardWidth, 25, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text('EXCURSION DETAILS', excursionX + 10, y + 16);

    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Title:', excursionX + 10, y + 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const titleLines = doc.splitTextToSize(booking.excursionDetails?.title || 'N/A', cardWidth - 50);
    doc.text(titleLines[0] || booking.excursionDetails?.title || 'N/A', excursionX + 35, y + 40);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Date:', excursionX + 10, y + 55);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(format(new Date(booking.excursionDetails?.excursionDate || Date.now()), 'MMM dd, yyyy'), excursionX + 35, y + 55);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Destination:', excursionX + 10, y + 70);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const destLines = doc.splitTextToSize(booking.excursionDetails?.destination || 'N/A', cardWidth - 70);
    doc.text(destLines[0] || booking.excursionDetails?.destination || 'N/A', excursionX + 65, y + 70);

    y += 130;

    // --- MEETING & TOUR INFORMATION ---
    doc.setFillColor(250, 250, 250);
    doc.rect(margin, y, pageWidth - (margin * 2), 140, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.rect(margin, y, pageWidth - (margin * 2), 140);

    doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.rect(margin, y, pageWidth - (margin * 2), 25, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text('MEETING & TOUR INFORMATION', margin + 15, y + 16);

    let detailsY = y + 45;
    doc.setTextColor(60, 60, 60);

    if (booking.excursionDetails?.tourOperator) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Tour Operator:', margin + 15, detailsY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const operatorLines = doc.splitTextToSize(booking.excursionDetails.tourOperator, pageWidth - (margin * 2) - 100);
      doc.text(operatorLines[0], margin + 90, detailsY);
      detailsY += 18;
    }

    if (booking.excursionDetails?.meetingPoint) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Meeting Point:', margin + 15, detailsY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const meetingLines = doc.splitTextToSize(booking.excursionDetails.meetingPoint, pageWidth - (margin * 2) - 100);
      doc.text(meetingLines[0], margin + 90, detailsY);
      detailsY += 18;
    }

    if (booking.excursionDetails?.meetingTime) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Meeting Time:', margin + 15, detailsY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(booking.excursionDetails.meetingTime, margin + 90, detailsY);
      detailsY += 18;
    }

    if (booking.excursionDetails?.dropOffLocation) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Drop-off Location:', margin + 15, detailsY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const dropOffLines = doc.splitTextToSize(booking.excursionDetails.dropOffLocation, pageWidth - (margin * 2) - 115);
      doc.text(dropOffLines[0], margin + 105, detailsY);
      detailsY += 18;
    }

    const leftCol = margin + 15;
    const rightCol = margin + 280;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Language:', leftCol, detailsY);
    doc.setFont('helvetica', 'normal');
    doc.text(booking.excursionDetails?.language || 'English', leftCol + 55, detailsY);

    doc.setFont('helvetica', 'bold');
    doc.text('Participants:', rightCol, detailsY);
    doc.setFont('helvetica', 'normal');
    doc.text(String(booking.excursionDetails?.participants || 1), rightCol + 65, detailsY);

    y += 160;

    // --- PRICING BREAKDOWN ---
    const pricingWidth = 280;
    const pricingX = pageWidth - margin - pricingWidth;

    doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.rect(pricingX, y, pricingWidth, 25, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text('PRICING BREAKDOWN', pricingX + pricingWidth / 2, y + 16, { align: 'center' });

    y += 25;

    let pricingHeight = 60;
    
    if (hasPricePerPerson) {
      if (booking.pricing?.adultCount > 0) pricingHeight += 18;
      if (booking.pricing?.childCount > 0) pricingHeight += 18;
    } else {
      pricingHeight += 18;
    }
    
    pricingHeight += 18;
    if (booking.pricing?.conciergeService > 0) pricingHeight += 18;

    doc.setFillColor(255, 255, 255);
    doc.rect(pricingX, y, pricingWidth, pricingHeight, 'F');
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.rect(pricingX, y, pricingWidth, pricingHeight);

    let pricingY = y + 20;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);

    if (hasPricePerPerson) {
      if (booking.pricing?.adultCount > 0) {
        doc.text(`Adults (${booking.pricing.adultCount}):`, pricingX + 15, pricingY);
        doc.text(`${currencySymbol}${(booking.pricing.adultPrice * booking.pricing.adultCount).toFixed(2)}`, pricingX + pricingWidth - 15, pricingY, { align: 'right' });
        pricingY += 18;
      }

      if (booking.pricing?.childCount > 0) {
        doc.text(`Children (${booking.pricing.childCount}):`, pricingX + 15, pricingY);
        doc.text(`${currencySymbol}${(booking.pricing.childPrice * booking.pricing.childCount).toFixed(2)}`, pricingX + pricingWidth - 15, pricingY, { align: 'right' });
        pricingY += 18;
      }
    } else {
      doc.text('Excursion (Group):', pricingX + 15, pricingY);
      doc.text(`${currencySymbol}${excursionTotal.toFixed(2)}`, pricingX + pricingWidth - 15, pricingY, { align: 'right' });
      pricingY += 18;
    }

    // Organization Fee (same as excursion total)
    doc.text('Organization Fee:', pricingX + 15, pricingY);
    
    if (booking.paymentStatus?.organizationFeePaid) {
      doc.setFillColor(34, 197, 94);
      doc.roundedRect(pricingX + pricingWidth - 90, pricingY - 8, 35, 12, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(255, 255, 255);
      doc.text('PAID', pricingX + pricingWidth - 72.5, pricingY, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
    }
    
    doc.text(`${currencySymbol}${excursionTotal.toFixed(2)}`, pricingX + pricingWidth - 15, pricingY, { align: 'right' });
    pricingY += 18;

    if (booking.pricing?.conciergeService > 0) {
      doc.text('Concierge Service:', pricingX + 15, pricingY);
      
      if (booking.paymentStatus?.conciergeServicePaid) {
        doc.setFillColor(34, 197, 94);
        doc.roundedRect(pricingX + pricingWidth - 90, pricingY - 8, 35, 12, 2, 2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(255, 255, 255);
        doc.text('PAID', pricingX + pricingWidth - 72.5, pricingY, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(60, 60, 60);
      }
      
      doc.text(`${currencySymbol}${booking.pricing.conciergeService.toFixed(2)}`, pricingX + pricingWidth - 15, pricingY, { align: 'right' });
      pricingY += 18;
    }

    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(1);
    doc.line(pricingX + 15, pricingY + 8, pricingX + pricingWidth - 15, pricingY + 8);
    pricingY += 20;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('TOTAL:', pricingX + 15, pricingY);
    doc.text(`${currencySymbol}${booking.total.toFixed(2)}`, pricingX + pricingWidth - 15, pricingY, { align: 'right' });

    y += pricingHeight + 40;

    // --- NEW PAGE FOR INCLUDES/EXCLUDES ---
    doc.addPage();
    y = margin;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('WHAT\'S INCLUDED & EXCLUDED', margin, y);
    y += 35;

    const columnWidth = (pageWidth - (margin * 2) - 30) / 2;

    // What's Included
    doc.setFillColor(240, 248, 255);
    doc.rect(margin, y, columnWidth, 200, 'F');
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.rect(margin, y, columnWidth, 200);

    doc.setFillColor(46, 204, 113);
    doc.rect(margin, y, columnWidth, 25, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text('✓ WHAT\'S INCLUDED', margin + 12, y + 16);

    let includesY = y + 45;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);

    if (booking.excursionDetails?.includes) {
      booking.excursionDetails.includes.forEach(item => {
        if (item.trim() && includesY < y + 185) {
          const lines = doc.splitTextToSize(`• ${item}`, columnWidth - 24);
          lines.forEach(line => {
            if (includesY < y + 185) {
              doc.text(line, margin + 12, includesY);
              includesY += 12;
            }
          });
        }
      });
    }

    // What's Not Included
    const excludesX = margin + columnWidth + 30;

    doc.setFillColor(255, 245, 245);
    doc.rect(excludesX, y, columnWidth, 200, 'F');
    doc.setDrawColor(231, 76, 60);
    doc.rect(excludesX, y, columnWidth, 200);

    doc.setFillColor(231, 76, 60);
    doc.rect(excludesX, y, columnWidth, 25, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text('✗ WHAT\'S NOT INCLUDED', excludesX + 12, y + 16);

    let excludesY = y + 45;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);

    if (booking.excursionDetails?.excludes) {
      booking.excursionDetails.excludes.forEach(item => {
        if (item.trim() && excludesY < y + 185) {
          const lines = doc.splitTextToSize(`• ${item}`, columnWidth - 24);
          lines.forEach(line => {
            if (excludesY < y + 185) {
              doc.text(line, excludesX + 12, excludesY);
              excludesY += 12;
            }
          });
        }
      });
    }

    y += 220;

    // --- TOUR OPERATOR INFORMATION ---
    if (booking.operatorInfo && (booking.operatorInfo.confirmationEmail || booking.operatorInfo.contractDetails)) {
      if (y > pageHeight - 150) {
        doc.addPage();
        y = margin;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text('TOUR OPERATOR INFORMATION', margin, y);
      y += 30;

      if (booking.operatorInfo.confirmationEmail) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(60, 60, 60);
        doc.text('Confirmation Email:', margin, y);
        y += 20;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        y = addWrappedText(booking.operatorInfo.confirmationEmail, margin, y, pageWidth - (margin * 2), 9, 11);
        y += 20;
      }

      if (booking.operatorInfo.contractDetails) {
        if (y > pageHeight - 100) {
          doc.addPage();
          y = margin;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(60, 60, 60);
        doc.text('Contract Details:', margin, y);
        y += 20;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        y = addWrappedText(booking.operatorInfo.contractDetails, margin, y, pageWidth - (margin * 2), 9, 11);
        y += 20;
      }

      if (booking.operatorInfo.cancellationPolicy) {
        if (y > pageHeight - 100) {
          doc.addPage();
          y = margin;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(60, 60, 60);
        doc.text('Cancellation Policy:', margin, y);
        y += 20;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        y = addWrappedText(booking.operatorInfo.cancellationPolicy, margin, y, pageWidth - (margin * 2), 9, 11);
        y += 20;
      }

      if (booking.operatorInfo.emergencyContact) {
        if (y > pageHeight - 50) {
          doc.addPage();
          y = margin;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(60, 60, 60);
        doc.text('Emergency Contact:', margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(booking.operatorInfo.emergencyContact, margin + 120, y);
      }
    }

    // --- FOOTER ---
    const footerY = pageHeight - 50;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(booking.companyInfo?.name || 'Travel Agency', pageWidth / 2, footerY, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    
    const contactInfo = [];
    if (booking.companyInfo?.email) contactInfo.push(booking.companyInfo.email);
    if (booking.companyInfo?.phone) contactInfo.push(booking.companyInfo.phone);
    
    if (contactInfo.length > 0) {
      doc.text(contactInfo.join(' • '), pageWidth / 2, footerY + 15, { align: 'center' });
    }
    
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text('Thank you for choosing our travel services! Have a wonderful excursion!', pageWidth / 2, footerY + 30, { align: 'center' });

    // --- SAVE ---
    const customerName = (booking.customer?.name || 'Customer').replace(/[^a-zA-Z0-9]/g, '_');
    const excursionDate = format(new Date(booking.excursionDetails?.excursionDate || Date.now()), 'yyyy-MM-dd');
    const filename = `${customerName}_excursion_booking_${bookingNumber.replace('-', '_')}_${excursionDate}.pdf`;

    doc.save(filename);

  } catch (error) {
    console.error('Error generating excursion PDF:', error);
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
};